import { App } from '@capacitor/app'
import { api } from '@/services/api'
import { getEnvValue, isNativePlatform } from '@/utils/platform'
import { isNewerVersion, isNewerVersionCode } from '@/utils/versionCompare'

export interface AppUpdateInfo {
  latestVersion: string
  latestVersionCode: number
  /** Preferred install URL: Play Store when available, else APK */
  installUrl: string
  apkDownloadUrl: string
  googlePlayUrl: string | null
  releaseNotes: string | null
  forceUpdate: boolean
  currentVersion: string
  currentBuild: string
  usesStore: boolean
}

/**
 * Check whether a newer APK is available.
 * Tradeoff: opens the APK/store URL in the system browser (user installs via the download),
 * rather than in-app PackageInstaller — simpler and avoids REQUEST_INSTALL_PACKAGES.
 */
export async function checkForAppUpdate(): Promise<AppUpdateInfo | null> {
  if (!isNativePlatform) return null

  try {
    const [info, response] = await Promise.all([
      App.getInfo(),
      api.getAppVersion(),
    ])

    if (!response.data.success || !response.data.data) return null

    const remote = response.data.data
    const currentVersion = info.version || '0.0.0'
    const currentBuild = info.build || '0'
    const currentCode = parseInt(currentBuild, 10) || 0

    const newerByCode = remote.latestVersionCode > 0
      && isNewerVersionCode(remote.latestVersionCode, currentCode)
    const newerByName = isNewerVersion(remote.latestVersion, currentVersion)

    if (!newerByCode && !newerByName) return null

    const storeUrl = (getEnvValue('VITE_PLAY_STORE_URL') || remote.googlePlayUrl || '').trim()
    const apkUrl = resolveApkUrl(remote.apkDownloadUrl)
    const usesStore = !!storeUrl
    const installUrl = storeUrl || apkUrl

    return {
      latestVersion: remote.latestVersion,
      latestVersionCode: remote.latestVersionCode,
      installUrl,
      apkDownloadUrl: apkUrl,
      googlePlayUrl: storeUrl || null,
      releaseNotes: remote.releaseNotes,
      forceUpdate: remote.forceUpdate,
      currentVersion,
      currentBuild,
      usesStore,
    }
  } catch (error) {
    console.warn('App update check failed:', error)
    return null
  }
}

export function resolveApkUrl(pathOrUrl: string): string {
  const envUrl = getEnvValue('VITE_APK_DOWNLOAD_URL')
  if (envUrl) return envUrl

  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
    return pathOrUrl
  }

  const apiBase = getEnvValue('VITE_API_BASE_URL') || ''
  if (apiBase) {
    try {
      const base = new URL(apiBase)
      return new URL(pathOrUrl, base.origin).toString()
    } catch {
      // fall through
    }
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return new URL(pathOrUrl, window.location.origin).toString()
  }

  return pathOrUrl
}

export function openApkDownload(url: string): void {
  // On native Android, navigate the WebView so the system can handle the APK download / store.
  // window.open is often blocked or opens an empty tab inside Capacitor.
  if (isNativePlatform) {
    window.location.assign(url)
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}
